/* ForgeSync faceted filtering (design: knowledge/2026-07-27-faceted-filtering-design.md).
 * Progressive enhancement: appends extra facet groups (per-collection config
 * served by ForgeSync) below the native S&D groups in the vertical facet
 * sidebar. Native page, native filters, and native grid stay untouched until
 * a ForgeSync facet is selected; clearing all selections restores the native
 * grid node. Any fetch failure = silent no-op, the native page still works.
 * State lives in the URL hash (#fsf=attr:v1|v2;attr2:v3;p:2) so it is
 * shareable and survives back/forward without creating crawlable URLs.
 */
(function () {
  'use strict';

  var BASE = '/apps/forgesync';
  var m = window.location.pathname.match(/\/collections\/([a-z0-9\-_]+)/);
  if (!m) return;
  var COLLECTION = m[1];

  // facets-vertical.liquid renders twice per collection page (desktop sidebar +
  // mobile drawer) and carries our script tag with it, so this runs twice.
  if (window.__forgesyncFacets) return;
  window.__forgesyncFacets = true;

  // NEVER cache these. The theme's own facets.js replaces
  // #Product-Grid-Container innerHTML and rebuilds the facet wrapper on every
  // native sort/filter, which detaches anything we held a reference to: our
  // groups vanished and the grid froze on native results until a full reload
  // (2026-07-28 sweep, finding 2). Re-query at use time instead.
  function getGridUl() { return document.querySelector('#Paginate-Content ul.content-list'); }
  // Both copies of facets-vertical (desktop sidebar + mobile drawer) have
  // their own .facets-main; the desktop one is small-hide medium-hide, so
  // rendering into only the first left phones and tablets with native alone.
  //
  // main-collection.liquid renders a THIRD .facets-main inside .facet-horizontal
  // (the View as / Sort by toolbar). That bar styles its children as a
  // horizontal row, so our vertical details/summary markup landed there as a
  // stack of unstyled labels in the top right ("Label Type, Package Qty, ..."
  // on /collections/labels, 2026-07-29). Vertical containers only.
  function getFacetsMains() {
    return Array.prototype.filter.call(
      document.querySelectorAll('.facets-main'),
      function (el) { return !el.closest('.facet-horizontal, .facet-horizontal-wrapper'); }
    );
  }
  if (!getGridUl() || !getFacetsMains().length) return;

  var nativeGridHTML = null; // captured before first swap
  var selections = {};       // attr_key -> [values]
  var page = 1;
  var swapped = false;       // have we actually replaced the native grid?
  var haveFacets = false;    // did the last good fetch return any groups for this collection?

  // Allan 2026-07-28: a zero-count option should not be offered at all. Native
  // S&D renders them disabled; ours never emits a zero. Injected from here so
  // the rule only applies when ForgeSync is actually driving the page.
  var zeroRule = document.createElement('style');
  zeroRule.textContent = '.facets-main .facet-checkbox--disabled{display:none}';
  document.head.appendChild(zeroRule);

  /* ---------- hash state ---------- */

  function readHash() {
    var h = window.location.hash.match(/fsf=([^&]*)/);
    selections = {}; page = 1;
    if (!h) return;
    decodeURIComponent(h[1]).split(';').forEach(function (part) {
      var kv = part.split(':');
      if (kv.length !== 2 || !kv[1]) return;
      if (kv[0] === 'p') { page = parseInt(kv[1], 10) || 1; return; }
      selections[kv[0]] = kv[1].split('|').filter(Boolean);
    });
  }

  function writeHash() {
    var parts = Object.keys(selections).filter(function (k) { return selections[k].length; })
      .map(function (k) { return k + ':' + selections[k].join('|'); });
    if (page > 1) parts.push('p:' + page);
    var url = window.location.pathname + window.location.search +
      (parts.length ? '#fsf=' + encodeURIComponent(parts.join(';')) : '');
    history.pushState(null, '', url);
  }

  function hasSelections() {
    return Object.keys(selections).some(function (k) { return selections[k].length; });
  }

  /* ---------- data ---------- */

  function facetsUrl() {
    // flat f=attr:v1|v2;attr2:v3 (values rawurlencoded) — bracket params break
    // the Shopify app-proxy signature
    var parts = Object.keys(selections).filter(function (k) { return selections[k].length; })
      .map(function (k) { return k + ':' + selections[k].map(encodeURIComponent).join('|'); });
    var params = ['collection=' + encodeURIComponent(COLLECTION), 'page=' + page];
    if (parts.length) params.push('f=' + encodeURIComponent(parts.join(';')));
    return BASE + '/facets?' + params.join('&');
  }

  function fetchJSON(url) {
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('facets ' + r.status);
      return r.json();
    });
  }

  /* ---------- rendering (mirrors native facet markup for free styling) ---------- */

  // attribute-safe escaping: values like [5" x 5"] must not break out
  function esc(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // Renders into every .facets-main (desktop sidebar and mobile drawer), each
  // with its own host node. idx keeps element ids unique across the copies.
  function renderGroups(facets) {
    Array.prototype.forEach.call(getFacetsMains(), function (main, idx) {
      var host = main.querySelector('.fs-facet-groups');
      if (!host) {
        host = document.createElement('div');
        host.className = 'fs-facet-groups';
        main.appendChild(host);
      }
      var open = {};
      host.querySelectorAll('details[data-fs-key]').forEach(function (d) {
        open[d.getAttribute('data-fs-key')] = d.open;
      });
      host.innerHTML = groupsHTML(facets, open, idx);
      hideNativeTwins(facets, main, host);
    });
  }

  function groupsHTML(facets, open, idx) {
    return facets.map(function (f) {
      var expandId = 'Expandable-FS-' + idx + '-' + esc(f.key);
      var selCount = f.values.filter(function (v) { return v.selected; }).length;
      return '<details-disclosure class="facet-item">' +
        '<details class="details-disclosure" data-fs-key="' + esc(f.key) + '"' + (open[f.key] || selCount ? ' open' : '') + '>' +
        '<summary class="facets-summary"><span>' + esc(f.label) +
        (selCount ? ' <span class="facets-selected">(' + selCount + ')</span>' : '') +
        '</span></summary>' +
        '<div class="facet-display details-disclosure-content" tabindex="-1">' +
        '<fieldset class="facet-display-content">' +
        '<legend class="visually-hidden">' + esc(f.label) + '</legend>' +
        // theme's own element: caps the list at 15rem and adds read more/less,
        // which bag_size needs at 65 values
        '<expandable-container id="' + expandId + '" data-limit-height style="--limit-height: 15rem">' +
        '<ul class="content-main list-unstyled" role="list">' +
        f.values.map(function (v) {
          return '<li class="facet-value checkbox"><label class="facet-checkbox' +
            (v.count === 0 && !v.selected ? ' facet-checkbox--disabled' : '') + '">' +
            // no name attr: keeps these out of the native facet form serialization
            '<input type="checkbox" data-fs-attr="' + esc(f.key) + '" value="' + esc(v.value) + '"' +
            (v.selected ? ' checked' : '') + (v.count === 0 && !v.selected ? ' disabled' : '') + '>' +
            '<span class="input-face"><span></span></span>' +
            '<span class="facet-value-name">' + esc(v.value) + '</span>' +
            '<span class="facet-value-count">' + (Number(v.count) || 0) + '</span>' +
            '</label></li>';
        }).join('') +
        '</ul>' +
        '<div class="expand-button-wrapper">' +
        '<button type="button" class="expand-button button--clear link link-text show-underline"' +
        ' aria-controls="' + expandId + '" aria-expanded="false">' +
        '<span class="read-more-text">Show more</span>' +
        '<span class="read-less-text hidden">Show less</span>' +
        '</button></div>' +
        '</expandable-container></fieldset></div></details></details-disclosure>';
    }).join('');
  }

  // Native S&D may filter on the same attribute we do (Bag Size, Color...).
  // Ours is per-collection and never shows zero-count values, so it supersedes
  // the native group: hide the twin instead of showing both. Only runs after a
  // successful render, so a ForgeSync outage leaves the native sidebar intact.
  function hideNativeTwins(facets, main, host) {
    var mine = {};
    facets.forEach(function (f) { mine[String(f.label).trim().toLowerCase()] = 1; });
    main.querySelectorAll('.facet-item').forEach(function (item) {
      if (host && host.contains(item)) return;
      var name = item.querySelector('.facet-name');
      if (!name) return;
      // strip the trailing "(2)" selected-count span before comparing
      var label = name.textContent.replace(/\(\d+\)\s*$/, '').trim().toLowerCase();
      item.style.display = mine[label] ? 'none' : '';
    });
  }

  function renderStatus(total, pages) {
    pages = Number(pages) || 0;
    var el = document.getElementById('fs-facet-status');
    if (!hasSelections()) { if (el) el.remove(); return; }
    if (!el) {
      el = document.createElement('div');
      el.id = 'fs-facet-status';
      el.className = 'facets-active';
      var ulForStatus = getGridUl();
      if (!ulForStatus) return;
      ulForStatus.parentNode.insertBefore(el, ulForStatus);
    }
    var pager = '';
    if (pages > 1) {
      pager = ' <span class="fs-facet-pager">' +
        (page > 1 ? '<button type="button" class="link" data-fs-page="' + (page - 1) + '">&larr;</button> ' : '') +
        'Page ' + page + '/' + pages +
        (page < pages ? ' <button type="button" class="link" data-fs-page="' + (page + 1) + '">&rarr;</button>' : '') +
        '</span>';
    }
    el.innerHTML = '<span>' + (Number(total) || 0) + ' products</span>' + pager +
      ' <button type="button" class="link show-underline" id="fs-facet-clear">Clear filters</button>';
  }

  function swapGrid(data) {
    var ul = getGridUl();
    if (!ul) return Promise.resolve();
    if (nativeGridHTML === null) nativeGridHTML = ul.innerHTML;
    var pagination = document.querySelector('#Paginate-Content .pagination-container');
    if (!hasSelections()) {
      // Only restore if we actually replaced the grid. Assigning innerHTML the
      // identical markup still destroys and recreates every <img> in the list,
      // which the browser repaints as a visible flash on every page load for
      // zero benefit (2026-07-29: "images having a dance party").
      if (swapped) { ul.innerHTML = nativeGridHTML; swapped = false; }
      if (pagination) pagination.style.display = '';
      renderStatus(0, 0);
      return Promise.resolve();
    }
    if (pagination) pagination.style.display = 'none';
    if (!data.handles.length) {
      ul.innerHTML = '<li class="grid-item"><p>No products match the selected filters.</p></li>';
      swapped = true;
      renderStatus(0, 0);
      return Promise.resolve();
    }
    return fetch(BASE + '/facets/grid?handles=' + encodeURIComponent(data.handles.join(',')))
      .then(function (r) { if (!r.ok) throw new Error('grid ' + r.status); return r.text(); })
      .then(function (html) {
        var target = getGridUl();
        if (target) { target.innerHTML = html; swapped = true; }
        renderStatus(data.total, data.pages);
      });
  }

  /* ---------- flow ---------- */

  var inflight = 0;
  function refresh() {
    var ticket = ++inflight;
    fetchJSON(facetsUrl()).then(function (data) {
      if (ticket !== inflight) return; // stale response
      haveFacets = !!(data.facets && data.facets.length);
      renderGroups(data.facets);
      return swapGrid(data);
    }).catch(function () { /* box down or error: leave native page alone */ });
  }

  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input.hasAttribute || !input.hasAttribute('data-fs-attr')) return;
    e.stopPropagation();
    var key = input.getAttribute('data-fs-attr');
    var list = selections[key] || (selections[key] = []);
    var idx = list.indexOf(input.value);
    if (input.checked && idx === -1) list.push(input.value);
    if (!input.checked && idx !== -1) list.splice(idx, 1);
    page = 1;
    writeHash();
    refresh();
  }, true);

  document.addEventListener('click', function (e) {
    if (e.target.id === 'fs-facet-clear') {
      selections = {}; page = 1;
      writeHash();
      refresh();
    }
    var p = e.target.getAttribute && e.target.getAttribute('data-fs-page');
    if (p) {
      page = parseInt(p, 10) || 1;
      writeHash();
      refresh();
    }
  });

  window.addEventListener('popstate', function () {
    readHash();
    refresh();
  });

  /* ---------- survive native re-renders ----------
   * The theme's facets.js rebuilds the grid container and the facet wrapper
   * on every native sort/filter, wiping our groups and leaving the grid on
   * native results while the hash still claims ForgeSync filters. Watch for
   * that and re-apply.
   *
   * The loop guard is haveFacets: "our markup is missing" is only meaningful
   * on a collection that HAS ForgeSync facets. Collections with no config get
   * a 200 with facets:[], render no [data-fs-attr] inputs ever, and so the
   * markup-missing test stayed true forever — every mutation we caused
   * re-entered refresh() and rewrote the grid, ~1/sec, flashing every product
   * image (2026-07-29). Check we have something to restore before restoring it.
   */
  function watchNativeRerender() {
    var host = document.querySelector('#Paginate-Content');
    var root = (host && host.parentNode) || document.body;
    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () {
        pending = null;
        if (!haveFacets) return;                               // nothing of ours on this collection
        if (document.querySelector('[data-fs-attr]')) return;  // our markup is intact
        if (!getGridUl() || !getFacetsMains().length) return;   // page not ready
        nativeGridHTML = null;   // whatever is in the grid now IS the fresh native list
        swapped = false;         // the theme rebuilt the grid, our swap is gone with it
        refresh();
      }, 60);
    }).observe(root, { childList: true, subtree: true });
  }

  readHash();
  refresh();
  watchNativeRerender();
})();
